import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { DigitalCardConfiguration, DigitalCardSettingsRequest, SettingsService } from '@src/app/shared/backend/clients';

type TextElementKey = 'productName' | 'sku' | 'serialNumber' | 'warrantyCode' | 'purchaseDate' | 'retailerName';
type TextAlign = 'left' | 'center' | 'right';
type DraggableElement = TextElementKey | 'qrCode';
type CardDimension = 'width' | 'height' | 'borderRadius';

type EditorTextElement = {
  active: boolean;
  xPercent: number;
  yPercent: number;
  color: string;
  fontSize: number;
  textAlign: TextAlign;
};

type EditorQrCodeElement = {
  active: boolean;
  xPercent: number;
  yPercent: number;
  size: number;
};

type EditorDigitalCardConfig = {
  background: {
    type: string;
    color: string;
  };
  card: {
    width: number;
    height: number;
    borderRadius: number;
  };
  elements: Record<TextElementKey, EditorTextElement> & {
    qrCode: EditorQrCodeElement;
  };
};

type DraggingElement = {
  key: DraggableElement;
  pointerOffsetX: number;
  pointerOffsetY: number;
  elementWidth: number;
  elementHeight: number;
};

@Component({
    selector: 'app-digital-card-settings',
    templateUrl: './digital-card-settings.component.html',
    styleUrls: ['./digital-card-settings.component.scss'],
    host: {
        class: 'app-digital-card-settings'
    },
    standalone: false
})
export class DigitalCardSettingsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() brandId!: number;
  @Output() settingsUpdated = new EventEmitter<void>();

  @ViewChild('editorPreview') editorPreviewWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('editorCard') editorCard?: ElementRef<HTMLDivElement>;
  @ViewChild('generatedPreview') generatedPreviewWrapper?: ElementRef<HTMLDivElement>;

  private readonly componentDestroyed$ = new Subject<void>();

  textElementKeys: TextElementKey[] = [
    'productName',
    'sku',
    'serialNumber',
    'warrantyCode',
    'purchaseDate',
    'retailerName'
  ];

  textElementLabels: Record<TextElementKey, string> = {
    productName: '',
    sku: '',
    serialNumber: '',
    warrantyCode: '',
    purchaseDate: '',
    retailerName: ''
  };

  textAlignDropdownOptions: {id: TextAlign, text: string}[] = [];

  isLoadingCardSettings = false;
  isCreatingDigitalCard = false;
  hasLoadedCardSettings = false;
  hasDigitalCard = false;

  showDeleteDigitalCardModalbox = false;
  isDeletingDigitalCard = false;

  cardConfig: EditorDigitalCardConfig = this.createDefaultConfig();
  cardBackground = '';
  backgroundImage: string | null = null;
  updateBackgroundImage = false;
  qrContent = '';
  savedPayload: DigitalCardConfiguration | null = null;
  fileUploadError = '';

  editorScale = 1;
  generatedScale = 1;

  textAlignDropdownValues: Record<TextElementKey, TextAlign[]> = this.createTextAlignDropdownValues(this.cardConfig);

  draggingElement: DraggingElement | null = null;
  editorResizeObserver?: ResizeObserver;
  generatedResizeObserver?: ResizeObserver;

  constructor(
    private zone: NgZone,
    private translate: TranslateService,
    private toasterService: ToasterService,
    private settingsService : SettingsService
  ) { }

  ngOnInit(): void {
    this.setTranslatedTexts();

    this.translate.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.setTranslatedTexts();
      });

    if (!this.brandId) {
      console.error('Brand ID is required to load digital card settings.');
      return;
    }

    this.loadExistingCardSettings();
  }

  ngAfterViewInit(): void {
    this.setupEditorResizeObserver();
    this.setupGeneratedResizeObserver();
  }

  async createDigitalCard(): Promise<void> {
    this.isCreatingDigitalCard = true;

    const request: DigitalCardSettingsRequest = {
      brandId: this.brandId,
      configuration: this.buildPayload(),
      qrContent: this.qrContent,
      backgroundImage: this.backgroundImage,
      updateBackgroundImage: this.updateBackgroundImage
    };

    try {
      await firstValueFrom(this.settingsService.apiSettingsDigitalCardSettingsUpsertPut(request));

      await this.loadExistingCardSettings();

      this.toasterService.show(
        'success',
        this.translate.instant('Success'),
        this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.DIGITAL_CARD_CREATED_MSG')
      );
    } catch (error) {
      console.error('Create digital card failed', error);

      this.toasterService.show(
        'error',
        this.translate.instant('Error'),
        this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.DIGITAL_CARD_FAILED_CREATED_MSG')
      );
    } finally {
      this.isCreatingDigitalCard = false;
    }
  }

  private setTranslatedTexts(): void {
    this.textElementLabels = {
      productName: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.PRODUCT_NAME'),
      sku: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.SKU'),
      serialNumber: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.SERIAL_NUMBER'),
      warrantyCode: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.WARRANTY_CODE'),
      purchaseDate: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.PURCHASE_DATE'),
      retailerName: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.RETAILER_NAME')
    };

    this.textAlignDropdownOptions = [
      { id: 'left', text: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.ALIGN_LEFT') },
      { id: 'center', text: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.ALIGN_CENTER') },
      { id: 'right', text: this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.ALIGN_RIGHT') }
    ];
  }

  trackByTextElementKey(_index: number, key: TextElementKey): TextElementKey {
    return key;
  }

  getTextElement(key: TextElementKey): EditorTextElement {
    return this.cardConfig.elements[key];
  }

  getTextElementLabel(key: TextElementKey): string {
    return this.textElementLabels[key];
  }

  isTextElementActive(key: TextElementKey): boolean {
    return this.cardConfig.elements[key].active;
  }

  setTextElementActive(key: TextElementKey, active: boolean): void {
    this.cardConfig.elements[key].active = active;
  }

  setTextElementFontSize(key: TextElementKey, value: number | string): void {
    this.cardConfig.elements[key].fontSize = Number(value) || 14;
  }

  getTextAlignDropdownValue(key: TextElementKey): TextAlign[] {
    return this.textAlignDropdownValues[key];
  }

  setTextAlignFromDropdown(key: TextElementKey, selectedValues: TextAlign[] | TextAlign | null): void {
    const selectedAlign = Array.isArray(selectedValues) ? selectedValues[0] : selectedValues;

    if (!this.isTextAlign(selectedAlign)) {
      return;
    }

    this.cardConfig.elements[key].textAlign = selectedAlign;
    this.textAlignDropdownValues[key] = [selectedAlign];
  }

  getBackgroundColor(): string {
    return this.cardConfig.background.color ?? '';
  }

  onBackgroundNativeColorChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.cardConfig.background.color = this.normaliseHexColor(input.value, '#f3f4f6');
  }

  onBackgroundHexInput(value: string): void {
    this.cardConfig.background.color = value;
  }

  onBackgroundHexBlur(): void {
    this.cardConfig.background.color = this.normaliseHexColor(this.cardConfig.background.color, '#f3f4f6');
  }

  getTextColor(key: TextElementKey): string {
    return this.cardConfig.elements[key].color ?? '';
  }

  onTextNativeColorChange(key: TextElementKey, event: Event): void {
    const input = event.target as HTMLInputElement;

    this.cardConfig.elements[key].color = this.normaliseHexColor(input.value, '#111827');
  }

  onTextHexInput(key: TextElementKey, value: string): void {
    this.cardConfig.elements[key].color = value;
  }

  onTextHexBlur(key: TextElementKey): void {
    this.cardConfig.elements[key].color = this.normaliseHexColor(this.cardConfig.elements[key].color, '#111827');
  }

  async onBackgroundImageUpload(event: any): Promise<void> {
    const file: File | undefined = event?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png'];
    const maxSizeBytes = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.fileUploadError = 'Only PNG files are allowed.';
      return;
    }

    if (file.size > maxSizeBytes) {
      this.fileUploadError = 'File size must be less than 10MB.';
      return;
    }

    this.fileUploadError = '';

    try {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      this.cardBackground = URL.createObjectURL(file);
      this.cardConfig.background.type = 'image';

      this.backgroundImage = JSON.stringify(Array.from(bytes));
      this.updateBackgroundImage = true;
    } catch (error) {
      console.error('Read digital card background failed', error);

      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.DIGITAL_CARD_FAILED_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.DIGITAL_CARD.DIGITAL_CARD_FAILED_UPLOADED_MSG')
      );
    }
  }

  removeBackgroundImage(): void {
    this.cardConfig.background.type = 'color';
    this.fileUploadError = '';
    this.cardBackground = '';

    this.backgroundImage = null;
    this.updateBackgroundImage = true;
  }

  onCardDimensionInputChange(): void {
    setTimeout(() => this.updateEditorScale());
  }

  onCardDimensionBlur(dimension: CardDimension): void {
    const dimensionRules = {
      width: { minimum: 1, fallback: 640 },
      height: { minimum: 1, fallback: 360 },
      borderRadius: { minimum: 0, fallback: 16 }
    };

    const rules = dimensionRules[dimension];
    const currentValue = Number(this.cardConfig.card[dimension]);
    const valueIsTooSmall = currentValue < rules.minimum;

    if (valueIsTooSmall) {
      this.cardConfig.card[dimension] = rules.fallback;
    } else {
      this.cardConfig.card[dimension] = currentValue;
    }

    setTimeout(() => this.updateEditorScale());
  }

  startDrag(event: PointerEvent, key: DraggableElement): void {
    const card = this.editorCard?.nativeElement;
    const target = event.currentTarget as HTMLElement;

    if (!card || !target) {
      return;
    }

    event.preventDefault();
    target.setPointerCapture?.(event.pointerId);

    const targetRect = target.getBoundingClientRect();

    this.draggingElement = {
      key,
      pointerOffsetX: event.clientX - targetRect.left,
      pointerOffsetY: event.clientY - targetRect.top,
      elementWidth: targetRect.width,
      elementHeight: targetRect.height
    };
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.draggingElement || !this.editorCard) {
      return;
    }

    const cardRect = this.editorCard.nativeElement.getBoundingClientRect();

    const maxX = Math.max(0, cardRect.width - this.draggingElement.elementWidth);
    const maxY = Math.max(0, cardRect.height - this.draggingElement.elementHeight);

    const x = this.clamp(event.clientX - cardRect.left - this.draggingElement.pointerOffsetX, 0, maxX);
    const y = this.clamp(event.clientY - cardRect.top - this.draggingElement.pointerOffsetY, 0, maxY);

    const xPercent = this.roundToTwoDecimals((x / cardRect.width) * 100);
    const yPercent = this.roundToTwoDecimals((y / cardRect.height) * 100);

    if (this.draggingElement.key === 'qrCode') {
      this.cardConfig.elements.qrCode.xPercent = xPercent;
      this.cardConfig.elements.qrCode.yPercent = yPercent;
      return;
    }

    this.cardConfig.elements[this.draggingElement.key].xPercent = xPercent;
    this.cardConfig.elements[this.draggingElement.key].yPercent = yPercent;
  }

  @HostListener('window:pointerup')
  onPointerUp(): void {
    this.draggingElement = null;
  }

  async saveSettings(): Promise<void> {
    const payload = this.buildPayload();

    try {
      const request: DigitalCardSettingsRequest = {
        brandId: this.brandId,
        configuration: payload,
        qrContent: this.qrContent,
        backgroundImage: this.backgroundImage,
        updateBackgroundImage: this.updateBackgroundImage
      };

      await firstValueFrom(this.settingsService.apiSettingsDigitalCardSettingsUpsertPut(request));

      this.savedPayload = request.configuration;
      this.backgroundImage = null;
      this.updateBackgroundImage = false;

      this.toasterService.show(
        'success',
        this.translate.instant('Success'),
        this.translate.instant('Card settings successfully updated.')
      );

      this.settingsUpdated.emit();
    } catch (error) {
      console.error('Update digital card settings failed', error);

      this.toasterService.show(
        'error',
        this.translate.instant('Error'),
        this.translate.instant('Card customisation error.')
      );
    }
  }

  clearSettings(): void {
    this.cardConfig = this.createDefaultConfig();
    this.textAlignDropdownValues = this.createTextAlignDropdownValues(this.cardConfig);

    this.qrContent = '';
    this.savedPayload = null;
    this.fileUploadError = '';

    setTimeout(() => this.updateEditorScale());
  }

  async deleteSettings(): Promise<void> {
    this.isDeletingDigitalCard = true;

    try {
      await firstValueFrom(
        this.settingsService.apiSettingsDigitalCardSettingsBrandIdDelete(
          this.brandId
        )
      );

      this.showDeleteDigitalCardModalbox = false;
      this.qrContent = '';
      this.savedPayload = null;
      this.fileUploadError = '';
      this.backgroundImage = null;
      this.updateBackgroundImage = false;

      this.toasterService.show(
        'success',
        this.translate.instant('Success'),
        this.translate.instant('Card settings successfully deleted.')
      );

      this.settingsUpdated.emit();
    } catch (error) {
      console.error('Delete digital card settings failed', error);

      this.toasterService.show(
        'error',
        this.translate.instant('Error'),
        this.translate.instant('Delete digital card settings failed.')
      );
    } finally {
      this.isDeletingDigitalCard = false;
    }
  }

  getEditorCardWidth(): number {
    return this.cardConfig.card.width || 640;
  }

  getEditorCardHeight(): number {
    return this.cardConfig.card.height || 360;
  }

  getCardBorderRadius(): number {
    return this.cardConfig.card.borderRadius ?? 16;
  }

  getEditorStageWidth(): number {
    return this.getEditorCardWidth() * this.editorScale;
  }

  getEditorStageHeight(): number {
    return this.getEditorCardHeight() * this.editorScale;
  }

  getSavedTextElement(key: TextElementKey): EditorTextElement | null {
    return this.savedPayload?.elements?.[key] as EditorTextElement ?? null;
  }


  getGeneratedStageWidth(): number {
    return this.savedPayload ? (this.savedPayload.card?.width ?? 640) * this.generatedScale : 0;
  }

  getGeneratedStageHeight(): number {
    return this.savedPayload ? (this.savedPayload.card?.height ?? 360) * this.generatedScale : 0;
  }

  getSavedCardBorderRadius(): number {
    return this.savedPayload?.card?.borderRadius ?? 16;
  }

  displayDeleteDigitalCardModalbox() {
    this.showDeleteDigitalCardModalbox = true;
  }

  cancelDeleteDigitalCard() {
    this.showDeleteDigitalCardModalbox = false;
  }

  private createDefaultConfig(): EditorDigitalCardConfig {
    return {
      background: {
        type: 'color',
        color: '#f3f4f6'
      },
      card: {
        width: 640,
        height: 360,
        borderRadius: 16
      },
      elements: {
        productName: this.createTextElement(false, 6, 16),
        sku: this.createTextElement(false, 40, 19),
        serialNumber: this.createTextElement(true, 6, 29),
        warrantyCode: this.createTextElement(true, 67, 55),
        purchaseDate: this.createTextElement(false, 6, 79),
        retailerName: this.createTextElement(false, 6, 89),
        qrCode: {
          active: true,
          xPercent: 69,
          yPercent: 16,
          size: 128
        }
      }
    };
  }

  private createTextElement(active: boolean, xPercent: number, yPercent: number): EditorTextElement {
    return {
      active,
      xPercent,
      yPercent,
      color: '',
      fontSize: 16,
      textAlign: 'left'
    };
  }

  private buildPayload(): DigitalCardConfiguration {
    this.onCardDimensionBlur('width');
    this.onCardDimensionBlur('height');
    this.onCardDimensionBlur('borderRadius');

    const elements: DigitalCardConfiguration['elements'] = {};

    this.textElementKeys.forEach((key) => {
      const element = this.cardConfig.elements[key];

      if (!element.active) {
        return;
      }

      elements[key] = {
        active: true,
        xPercent: element.xPercent,
        yPercent: element.yPercent,
        color: element.color,
        fontSize: Number(element.fontSize) || 14,
        textAlign: element.textAlign
      };
    });

    if (this.cardConfig.elements.qrCode.active) {
      elements.qrCode = {
        active: true,
        xPercent: this.cardConfig.elements.qrCode.xPercent,
        yPercent: this.cardConfig.elements.qrCode.yPercent,
        size: Number(this.cardConfig.elements.qrCode.size) || 128
      };
    }

    return {
      background: {
        type: this.cardConfig.background.type || 'color',
        color: this.cardConfig.background.color
      },
      card: {
        width: this.cardConfig.card.width || 640,
        height: this.cardConfig.card.height || 360,
        borderRadius: this.cardConfig.card.borderRadius ?? 16
      },
      elements
    };
  }

  private async loadExistingCardSettings(): Promise<void> {
    this.isLoadingCardSettings = true;
    this.hasLoadedCardSettings = false;
    this.hasDigitalCard = false;

    this.backgroundImage = null;
    this.updateBackgroundImage = false;

    try {
      const response = await firstValueFrom(this.settingsService.apiSettingsDigitalCardSettingsBrandIdGet(this.brandId));

      // handle "empty card" response
      this.hasDigitalCard = (response.id ?? 0) > 0;

      if (!this.hasDigitalCard || !response.configuration) {
        return;
      }

      this.savedPayload = response.configuration;
      this.qrContent = response.qrContent ?? '';
      this.cardBackground = response.backgroundImage ? `data:image/png;base64,${response.backgroundImage}` : '';

      this.cardConfig = this.createEditorConfigFromPayload(
        response.configuration
      );

      this.textAlignDropdownValues =
        this.createTextAlignDropdownValues(this.cardConfig);

      setTimeout(() => {
        this.updateEditorScale();
        this.setupEditorResizeObserver();
        this.setupGeneratedResizeObserver();
      });
    } catch (error) {
      console.error('Load digital card settings failed', error);
    } finally {
      this.hasLoadedCardSettings = true;
      this.isLoadingCardSettings = false;
    }
  }

  private createEditorConfigFromPayload(payload: DigitalCardConfiguration): EditorDigitalCardConfig {
    const config = this.createDefaultConfig();

    config.background = {
      type: payload.background?.type || 'color',
      color: payload.background?.color || '#f3f4f6'
    };

    config.card = {
      width: payload.card?.width || 640,
      height: payload.card?.height || 360,
      borderRadius: payload.card?.borderRadius ?? 16
    };

    this.textElementKeys.forEach((key) => {
      const savedElement = payload.elements?.[key];

      config.elements[key].active = false;

      if (!savedElement) {
        return;
      }

      config.elements[key] = {
        active: savedElement.active ?? false,
        xPercent: savedElement.xPercent ?? 0,
        yPercent: savedElement.yPercent ?? 0,
        color: savedElement.color ?? '',
        fontSize: savedElement.fontSize ?? 16,
        textAlign: this.isTextAlign(savedElement.textAlign) ? savedElement.textAlign : 'left'
      };
    });

    config.elements.qrCode.active = false;

    if (payload.elements?.qrCode) {
      config.elements.qrCode = {
        active: payload.elements.qrCode.active ?? false,
        xPercent: payload.elements.qrCode.xPercent ?? 69,
        yPercent: payload.elements.qrCode.yPercent ?? 16,
        size: payload.elements.qrCode.size ?? 128
      };
    }

    return config;
  }

  private setupEditorResizeObserver(): void {
    this.editorResizeObserver?.disconnect();

    const wrapper = this.editorPreviewWrapper?.nativeElement;

    if (!wrapper || typeof ResizeObserver === 'undefined') {
      this.updateEditorScale();
      return;
    }

    this.editorResizeObserver = new ResizeObserver(() => {
      this.zone.run(() => this.updateEditorScale());
    });

    this.editorResizeObserver.observe(wrapper);
    this.updateEditorScale();
  }

  private updateEditorScale(): void {
    const wrapper = this.editorPreviewWrapper?.nativeElement;

    if (!wrapper) {
      this.editorScale = 1;
      return;
    }

    this.editorScale = this.calculateScale(wrapper, this.getEditorCardWidth());
  }

  private setupGeneratedResizeObserver(): void {
    this.generatedResizeObserver?.disconnect();

    const wrapper = this.generatedPreviewWrapper?.nativeElement;

    if (!wrapper || typeof ResizeObserver === 'undefined') {
      this.updateGeneratedScale();
      return;
    }

    this.generatedResizeObserver = new ResizeObserver(() => {
      this.zone.run(() => this.updateGeneratedScale());
    });

    this.generatedResizeObserver.observe(wrapper);
    this.updateGeneratedScale();
  }

  private updateGeneratedScale(): void {
    const wrapper = this.generatedPreviewWrapper?.nativeElement;

    if (!wrapper || !this.savedPayload) {
      this.generatedScale = 1;
      return;
    }

    this.generatedScale = this.calculateScale(wrapper, this.savedPayload.card?.width || 1);
  }

  private calculateScale(wrapper: HTMLElement, cardWidth: number): number {
    const styles = window.getComputedStyle(wrapper);
    const horizontalPadding = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
    const availableWidth = Math.max(0, wrapper.clientWidth - horizontalPadding);

    if (!availableWidth) {
      return 1;
    }

    return Math.min(1, availableWidth / (cardWidth || 1));
  }

  private createTextAlignDropdownValues(config: any): Record<TextElementKey, TextAlign[]> {
    return {
      productName: [config.elements.productName.textAlign],
      sku: [config.elements.sku.textAlign],
      serialNumber: [config.elements.serialNumber.textAlign],
      warrantyCode: [config.elements.warrantyCode.textAlign],
      purchaseDate: [config.elements.purchaseDate.textAlign],
      retailerName: [config.elements.retailerName.textAlign]
    };
  }

  private isTextAlign(value: unknown): value is TextAlign {
    return value === 'left' || value === 'center' || value === 'right';
  }

  private normaliseHexColor(value: string, fallback: string): string {
    const hex = value?.trim();
    return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : fallback;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100;
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();

    this.editorResizeObserver?.disconnect();
    this.generatedResizeObserver?.disconnect();
  }
}
